import { useMutation, useQueryClient } from '@tanstack/react-query';

import { adminFetch } from '@/hooks/admin/useAdminApi';

/**
 * Paying across a chain's stores from the master's accounting login.
 *
 * ⚠ The backend charges ONE PaymentIntent PER STORE (see
 * customer-group-pay-invoices for why), so a run can partly succeed. Nothing
 * here pretends otherwise: `stores[]` carries a per-store outcome, and
 * `remaining` means the budget ran out mid-run and the caller must call again
 * with exactly those ids.
 */

export interface GroupPayMethod {
  stripe_pm_id: string;
  kind: string;
  brand: string | null;
  last4: string | null;
  /** The master's MAIN PAYER — what a run charges when nobody picks. */
  is_default: boolean;
}

export interface GroupPayQuoteStore {
  customer_id: string;
  store_name: string;
  store_code: string | null;
  amount: number;
  invoice_count: number;
}

export interface GroupPayQuote {
  ok: true;
  dry_run: true;
  total: number;
  store_count: number;
  invoice_count: number;
  stores: GroupPayQuoteStore[];
  skipped_in_flight: string[];
  /** Rentals autopay will charge — refused server-side, never charged here. */
  payments_live: boolean;
  pay_from: { customer_id: string; name: string };
  /** null when the master has saved methods but has designated none. */
  default_pm_id: string | null;
  methods: GroupPayMethod[];
  charge_note?: string;
}

export interface GroupPayStoreResult {
  customer_id: string;
  store_name: string;
  store_code: string | null;
  amount: number;
  status: 'succeeded' | 'processing' | 'failed' | 'skipped';
  qbo_booked?: boolean;
  error?: string;
}

export interface GroupPayResult {
  ok: true;
  dry_run: false;
  charged_total: number;
  store_count: number;
  succeeded: number;
  processing: number;
  failed: number;
  stores: GroupPayStoreResult[];
  remaining: string[];
  skipped_in_flight: string[];
}

function post<T>(body: Record<string, unknown>): Promise<T> {
  return adminFetch<T>('customer-group-pay-invoices', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function useGroupPayQuote() {
  return useMutation<GroupPayQuote, Error, string[]>({
    mutationFn: (invoiceIds) => post<GroupPayQuote>({ invoice_ids: invoiceIds, dry_run: true }),
  });
}

/**
 * Run the payment. Loops on `remaining` so a chain wide enough to exceed the
 * function's time budget still finishes in one click — the backend's
 * already-being-paid guard is what makes the follow-up call safe.
 */
export function useGroupPay() {
  const qc = useQueryClient();
  return useMutation<GroupPayResult, Error, { invoiceIds: string[]; pmId: string }>({
    mutationFn: async ({ invoiceIds, pmId }) => {
      let pending = invoiceIds;
      let combined: GroupPayResult | null = null;
      // Bounded: each pass must charge at least one store or the backend
      // returns everything as `remaining`, which would spin forever.
      for (let pass = 0; pass < 12 && pending.length > 0; pass++) {
        const res = await post<GroupPayResult>({
          invoice_ids: pending,
          stripe_pm_id: pmId,
          dry_run: false,
        });
        combined = combined
          ? {
              ...res,
              charged_total: Number((combined.charged_total + res.charged_total).toFixed(2)),
              store_count: combined.store_count + res.store_count,
              succeeded: combined.succeeded + res.succeeded,
              processing: combined.processing + res.processing,
              failed: combined.failed + res.failed,
              stores: [...combined.stores, ...res.stores],
              skipped_in_flight: [...combined.skipped_in_flight, ...res.skipped_in_flight],
            }
          : res;
        if (res.remaining.length >= pending.length) break; // no progress — stop
        pending = res.remaining;
      }
      return { ...(combined as GroupPayResult), remaining: pending };
    },
    onSuccess: () => {
      // Balances, the chain list and the ordinary invoice views all move.
      qc.invalidateQueries({ queryKey: ['group-invoices'] });
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['pending-payments'] });
    },
  });
}

/**
 * Designate the master's MAIN PAYER from the pay screen.
 *
 * Writes the master's own `is_default` through the chain-AP gate, so the
 * person who pays the chain can set it without going to Settings on the
 * master — and it is the SAME flag Settings sets, not a chain-only copy.
 */
export function useSetGroupDefaultMethod() {
  return useMutation<{ ok: true; default_pm_id: string }, Error, string>({
    mutationFn: (pmId) =>
      post<{ ok: true; default_pm_id: string }>({ action: 'set_default', stripe_pm_id: pmId }),
  });
}
